import {
  ClientWorkFlowStatus,
  InvoiceStatus
} from "@wingspanhq/payments/dist/interfaces";
import { WSQueryCache } from "@ws-react-query";
import {
  BulkActionOnProgress,
  BulkActionProgress,
  bulkActionFactory,
  useForegroundBulkAction
} from "../../../modules/BulkActions";
import { QUERY_PAYABLE, QUERY_PAYABLES } from "../../../query/payments/keys";
import { paymentsService } from "../../../services/payments";
import { PayablesTableItemData } from "./PayablesTable";

function processApprovePayables(
  data: PayablesTableItemData[],
  onProgress: BulkActionOnProgress,
  onSuccess?: () => void
): Promise<BulkActionProgress> {
  const approvable = data.filter(
    item => item.payable?.status !== InvoiceStatus.Paid
  );
  const skipped = data.length - approvable.length;

  const bulkAction = bulkActionFactory<PayablesTableItemData>({
    onProgress,
    action: async item => {
      const payableId = item.payable?.payableId || "";

      return await paymentsService.payable.update(payableId, {
        client: { workflowStatus: ClientWorkFlowStatus.Approved }
      });
    },
    onFinish: ({ successItems }) => {
      WSQueryCache.invalidateQueries(QUERY_PAYABLES);
      successItems.forEach(item => {
        WSQueryCache.invalidateQueries([
          QUERY_PAYABLE,
          item.payable?.payableId
        ]);
      });
      onSuccess?.();
    }
  });

  return bulkAction.run(approvable).then(({ successItems, failedItems }) => ({
    total: data.length,
    processed: successItems.length + failedItems.length + skipped,
    successful: successItems.length,
    failed: failedItems.length
  }));
}

export function useBulkApprovePayables() {
  const runBulkAction = useForegroundBulkAction();

  return (items: PayablesTableItemData[], onSuccess?: () => void) => {
    const pluralized = `payable${items.length > 1 ? "s" : ""}`;

    return runBulkAction({
      actionName: "Approve payables",
      itemCount: items.length,
      confirmDescription: `Approve ${pluralized} to be paid`,
      confirmLabel: "Approve",
      prepare: async () => items,
      process: async (data, onProgress) =>
        processApprovePayables(data, onProgress, onSuccess)
    });
  };
}
